import type { ReactNode } from 'react';

import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import type { User } from '@/utils/session/fetch-user';

import { BASE_PATH } from '@/constants/config';
import { answeredSignalsFromChoices } from '@/lib/consent/consent-signals';
import { useConsent } from '@/lib/consent/use-consent';

import { AnalyticsClient } from './analytics-client';
import { AnalyticsContext } from './internal/analytics-context';
import { getOrCreateAnalyticsClient } from './internal/build-analytics-client';
import { deriveSide } from './internal/derive-side';
import { useSessionApplicableFeatureFlags } from './session-applicable-feature-flags';

/**
 * Flag targeting is product delivery, not analytics. A researcher who
 * has not granted analytics still needs `/flags` evaluated against
 * their user/org, but that identify must not fan out to GA/Segment.
 */
const FEATURE_FLAG_IDENTIFY_DESTINATIONS = {
  posthog: true,
  ga: false,
  gtm: false,
  segment: false,
  firstParty: false,
} as const;

interface AnalyticsProviderProps {
  children: ReactNode;
  apiKey: string;
  googleAnalyticsMeasurementId: string;
  googleTagManagerContainerId: string;
  segmentWriteKey: string;
  firstPartyWriteKey: string;
  firstPartyHost: string;
  environment: string;
  user: User | null;
  organizationId: string | null;
  teamId: string | null;
}

export function AnalyticsProvider({
  children,
  apiKey,
  googleAnalyticsMeasurementId,
  googleTagManagerContainerId,
  segmentWriteKey,
  firstPartyWriteKey,
  firstPartyHost,
  environment,
  user,
  organizationId,
  teamId,
}: AnalyticsProviderProps) {
  const router = useRouter();
  const {
    isResolved: isConsentResolved,
    signals: consentSignals,
    answers: consentAnswers,
  } = useConsent();
  const sessionApplicableFeatureFlags = useSessionApplicableFeatureFlags(
    user,
    organizationId,
  );

  // Refs read by the AnalyticsClient's getContext closure so route() always
  // sees the latest user without recreating the client. Pathname is read
  // directly from the router's mutable state on each call.
  const userRef = useRef<User | null>(user);
  userRef.current = user;

  // Lazy useRef init: build the client once, on the first render. The
  // module-scoped cache inside `getOrCreateAnalyticsClient` ensures Strict
  // Mode's intentional double-mount doesn't construct twice.
  const clientRef = useRef<AnalyticsClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = getOrCreateAnalyticsClient({
      env: {
        POSTHOG_API_KEY: apiKey,
        GOOGLE_ANALYTICS_MEASUREMENT_ID: googleAnalyticsMeasurementId,
        GOOGLE_TAG_MANAGER_CONTAINER_ID: googleTagManagerContainerId,
        SEGMENT_WRITE_KEY: segmentWriteKey,
        FIRST_PARTY_ANALYTICS_WRITE_KEY: firstPartyWriteKey,
        FIRST_PARTY_ANALYTICS_HOST: firstPartyHost,
        ENVIRONMENT: environment,
      },
      getUser: () => userRef.current,
      getPathname: () => router.state.location.pathname,
    });
  }
  const client = clientRef.current;

  const [consentResolved, setConsentResolved] = useState(false);
  // Publish consent and resolve identify, then signal via `consentResolved`
  // so the page-view effect can gate on it. The privacy invariant (no
  // pageview before consent is resolved) is enforced by data flow rather
  // than useEffect declaration order — refactors that reorder or extract
  // these effects can't silently leak pageviews to unconsented users.
  //
  // The consent state itself comes from `ConsentProvider` (`rio_consent`,
  // with the profile copy as a fallback). This effect waits for it:
  // publishing before resolution would broadcast deny-all over a real
  // stored consent.
  useEffect(() => {
    if (!isConsentResolved) return;
    try {
      // Full Consent Mode v2 signal set, on every pass. Sent before
      // `identify` so GA's consent state is settled before the
      // user_id/user_properties call goes out.
      //
      // The answers travel with the signals, which are the only place the
      // implied defaults appear: a destination that holds events until
      // consent is known has to tell the visitor's refusal from a category
      // they have not been asked about, and the signals alone read both as
      // `denied`.
      client.setConsent(
        consentSignals,
        answeredSignalsFromChoices(consentAnswers),
      );

      // Read off the signals, which are the consent in force for this
      // pageview: an implied region default counts here, the same way it does
      // for gtag. PostHog and Segment are destinations, not writers — nothing
      // in this effect persists a choice.
      const analyticsGranted = consentSignals.analytics_storage === 'granted';

      if (user) {
        const identifyTraits = {
          roles: user.roles,
          domain: user.email.split('@')[1],
          side: deriveSide(user, router.state.location.pathname),
          organizationId: organizationId ?? undefined,
          teamId: teamId ?? undefined,
          ...(user.roles.includes('researcher') &&
          sessionApplicableFeatureFlags.isSettled
            ? {
                sessionApplicableFeatureFlags:
                  sessionApplicableFeatureFlags.data,
              }
            : {}),
        };
        if (analyticsGranted) {
          client.identify(user.id, {
            email: user.email,
            name: user.name,
            ...identifyTraits,
          });
        } else if (user.roles.includes('researcher')) {
          // Client flags (`useFeatureFlag`) read posthog-js. Load
          // identity for targeting without writing analytics cookies
          // or sending events — PostHog stays in memory + opted out.
          client.identify(user.id, identifyTraits, {
            destinations: FEATURE_FLAG_IDENTIFY_DESTINATIONS,
          });
        }
        // Business rule: session recording is researcher-only AND
        // requires analytics consent. The decision has to live in app
        // code (not PostHog's project rules) because participants are
        // intentionally never identified to PostHog, so PostHog has no
        // `side` property to condition on. A researcher who opts out
        // must have any in-flight recording explicitly stopped —
        // withdrawing consent alone does not stop session recording.
        // Non-researchers are skipped entirely (the lazy-load adapter
        // never loads posthog-js on participant-only sessions, so
        // there's nothing to start or stop; in-session role transitions
        // don't happen).
        if (user.roles.includes('researcher')) {
          if (analyticsGranted) {
            client.toggleSessionRecording('on');
          } else {
            client.toggleSessionRecording('off', {
              destinations: { posthog: true },
            });
          }
        }
      } else {
        client.reset();
        // Same rule on logout: posthog.reset() rotates the distinct_id
        // but doesn't stop an in-flight recording, so we have to stop
        // it explicitly or the rest of the (now anonymous) tab session
        // keeps streaming to PostHog.
        client.toggleSessionRecording('off', {
          destinations: { posthog: true },
        });
      }
    } finally {
      // Always flip the gate — even if an adapter throws above, we don't
      // want the page-view effect to be stuck "off" forever. The client's
      // dispatch wraps adapter calls in try/catch so this finally is
      // belt-and-braces.
      setConsentResolved(true);
    }
  }, [
    user,
    client,
    router,
    isConsentResolved,
    consentSignals,
    consentAnswers,
    organizationId,
    teamId,
    sessionApplicableFeatureFlags.data,
    sessionApplicableFeatureFlags.isSettled,
  ]);

  // Page view subscription. Gated on `consentResolved` so the initial
  // pageview can't fire before the identify/consent effect has set
  // adapter opt-in/opt-out state.
  useEffect(() => {
    if (!consentResolved) return;

    const initialPathname = router.state.location.pathname;
    const initialPageLocation = getCurrentPageLocation();
    client.screen({
      name: initialPathname,
      attributes: {
        path: initialPathname,
        pageLocation: initialPageLocation,
        previousPageLocation: getDocumentReferrer(),
        title: getDocumentTitle(),
      },
    });

    const unsubscribe = router.subscribe(
      'onResolved',
      ({ fromLocation, toLocation }) => {
        const pageLocation = getPageLocationFromRouterLocation(toLocation);
        client.screen({
          name: toLocation.pathname,
          attributes: {
            path: toLocation.pathname,
            pageLocation,
            previousPageLocation:
              getPageLocationFromRouterLocation(fromLocation),
            title: getDocumentTitle(),
          },
        });
      },
    );

    return unsubscribe;
  }, [client, router, consentResolved]);

  return (
    <AnalyticsContext.Provider value={client}>
      {children}
    </AnalyticsContext.Provider>
  );
}

function getCurrentPageLocation(): string | undefined {
  if (globalThis.window === undefined) return undefined;
  const href = globalThis.window.location.href;
  const hashIndex = href.indexOf('#');
  return hashIndex === -1 ? href : href.slice(0, hashIndex);
}

function getPageLocationFromRouterLocation(
  location:
    | {
        href: string;
      }
    | undefined,
): string | undefined {
  if (!location) return undefined;
  if (globalThis.window === undefined) return undefined;

  const href = location.href;
  const hashIndex = href.indexOf('#');
  const raw = hashIndex === -1 ? href : href.slice(0, hashIndex);
  if (/^https?:\/\//i.test(raw)) return raw;

  const origin = globalThis.window.location.origin;
  const path = raw.startsWith(BASE_PATH) ? raw : `${BASE_PATH}${raw}`;
  return `${origin}${path}`;
}

function getDocumentReferrer(): string | undefined {
  if (globalThis.document === undefined) return undefined;
  return globalThis.document.referrer || undefined;
}

function getDocumentTitle(): string | undefined {
  if (globalThis.document === undefined) return undefined;
  return globalThis.document.title || undefined;
}
